import { Dispatch } from 'redux';

import { action, IDispatch } from '../tools/reduxTools';
import { EReferentials, ISearchResponse, IUserBrief, IReferencial, EContextList, IDBReferencialIdentity } from '../entities/IGlobal';
import { IReferentialsIdentities } from '../entities/IClusters';
import { EProfileDataType } from '../tools/profileConstants';
import { ISearchReferentialElements } from '../entities/IProfile';
import { IGlobalTypes, SET_REFERENTIAL, SET_PROGRESS_DATA } from './actionTypes/globalTypes';
import { IState } from '../reducers';
import { Http } from '../tools/http';
import { ELcid } from '../entities/ILanguage';
import { addData, EDBNames, getStoreDataByKey, initDB, Stores } from '../tools/db';
import { removeArrDuplicates } from '../tools/arrayTools';

interface IDbReferential { id: string, key: string, nameLabels: { label: string, lcid: ELcid }[], parentClientReferentialIds: [] }

export const getReferentialsChildren = (query: string = '', referentialIdentity: string, parent?: string) => action<Promise<IReferencial[]>>((dispatch: Dispatch, getState: () => IState, http: Http) => {
    const { userLanguage } = getState().language;
    return http.profilesApiGet(`/referentials/elements/${referentialIdentity}/children?size=9999&lcid=${userLanguage}${query.length > 0 ? `&query=${query}` : ''}${parent ? `&parent=${parent}` : ''}`);
});

export const getAllReferentials = () => action<Promise<IReferencial[]>>(async (dispatch: Dispatch, getState: () => IState, http: Http) => {
    const status = await initDB(EDBNames.REFERENTIALSDB);
    return http.profilesApiGet(`/referentials/identities-with-elements`, undefined, undefined, undefined, Stores.ALLREFERENTIALS).then(async (res) => {
        const referentialsIdsArr = res.map(elem => elem.referentialElements).flat();
        for (let i = 0; i < referentialsIdsArr.length; i++) {
            await addData(Stores.ALLREFERENTIALSIDS, referentialsIdsArr[i], referentialsIdsArr[i].id, EDBNames.REFERENTIALSDB);
        }
        const contexts: string[] = removeArrDuplicates(res.map(elem => elem.context));
        if (status && contexts.length > 0) {
            for (let i = 0; i < contexts.length; i++) {
                const filteredData = res.filter(elem => elem.context === contexts[i]);
                for (let y = 0; y < filteredData.length; y++) {
                    const finalData = Object.fromEntries(filteredData.map(el => [el.referentialType, el]));
                    if (finalData) {
                        await addData(Stores.ALLREFERENTIALS, finalData, contexts[i], EDBNames.REFERENTIALSDB);
                    }
                }
            }
        }
    });
});

const initReferentialsWithNetworkRequest = (query: string = '', type: EProfileDataType | EReferentials, context: EContextList, parent: string, dispatch: Dispatch, getState: () => IState, http: Http) => {
    const { userLanguage } = getState().language;
    const { referentials } = getState().global;

    let obj = referentials || {};
    let isExistContext = !!obj?.[context];
    let isExistType = !!obj?.[context]?.[type];

    return http.profilesApiGet(`/referentials/contexts/${context}/types/${type}/elements?size=9999&lcid=${userLanguage}${query.length > 0 ? `&query=${query}` : ''}${parent ? `&parent=${parent}` : ''}`).then((response: ISearchReferentialElements) => {
        if (isExistContext) {
            if (!isExistType) {
                obj = { ...obj, [context]: { ...obj[context], [type]: response.items } };
            }
        } else {
            obj = { ...obj, [context]: { [type]: response.items } };
        }

        dispatch<IDispatch<IGlobalTypes['SET_REFERENTIAL']>>({
            type: SET_REFERENTIAL,
            referentialsList: obj
        });

        return response.items;
    });
};

export const getReferentialsWithMetadata = (query: string = '', type: EProfileDataType | EReferentials, context: EContextList = EContextList.Data, parent?: string) => action<Promise<any>>((dispatch: Dispatch, getState: () => IState, http: Http) => {
    return getStoreDataByKey(Stores.ALLREFERENTIALS, context).then((res: IDbReferential[]) => {
        if (res?.[type]) {
            return res[type];
        } else {
            return initReferentialsWithNetworkRequest(query, type, context, parent, dispatch, getState, http);
        }
    });
});

export const getReferentials = (query: string = '', type: EProfileDataType | EReferentials, context: EContextList = EContextList.Data, parent?: string, bustCache?: boolean) => action<Promise<IReferencial[]>>((dispatch: Dispatch, getState: () => IState, http: Http) => {
    const { userLanguage } = getState().language;
    const { referentials } = getState().global;

    return getStoreDataByKey(Stores.ALLREFERENTIALS, context).then((res: IDbReferential[]) => {
        if (res?.[type]?.referentialElements?.length > 0) {
            const filteredData = parent ? res[type].referentialElements.filter(element => element?.parentClientReferentialIds?.includes(parent)) : res[type].referentialElements;
            return filteredData?.map((elem: IDbReferential) => ({
                Key: elem.key,
                id: elem.id,
                // tslint:disable-next-line:no-null-keyword
                logicalId: null,
                name: elem.nameLabels.filter(label => label.lcid === userLanguage)?.[0]?.label || '',
                parents: elem.parentClientReferentialIds
            })).sort((a, b) => a.name.localeCompare(b.name));
        } else {
            let obj = referentials || {};
            let isExistContext = !!obj?.[context];
            let isExistType = !!obj?.[context]?.[type];
            if (!isExistContext || !isExistType || parent || bustCache) {
                return initReferentialsWithNetworkRequest(query, type, context, parent, dispatch, getState, http);
            }
            if (referentials?.[context]?.[type]) {
                return referentials?.[context]?.[type]?.filter(elem => elem?.name?.toLowerCase()?.includes(query?.toLowerCase()));
            } else {
                return [];
            }
        }
    });
});

export const getUnits = () => action<Promise<IReferencial[]>>(async (dispatch, getState, http) => {
    const currency = await dispatch(getReferentials('', EReferentials.Currency, EContextList.Platform));
    const percentage = await dispatch(getReferentials('', EReferentials.Percentage, EContextList.Platform));
    const duration = await dispatch(getReferentials('', EReferentials.Duration, EContextList.Platform));
    return [...currency, ...percentage, ...duration];
});

export const getReferentialsIdentities = (query: string = '') => action<Promise<IReferentialsIdentities[]>>((dispatch, getState, http) => {
    return http.profilesApiGet(`/referentials/identities?size=9999${query.length > 0 ? `&query=${query}` : ''}`).then((res: ISearchResponse<IReferentialsIdentities>) => res.items);
});

export const getMultipleUsersById = (body: string[]) => action<Promise<IUserBrief[]>>((dispatch: Dispatch, getState: () => IState, http: Http) => {
    const { userLanguage } = getState().language;
    return http.profilesApiPost(`/users/by-ids?lcid=${userLanguage}`, body);
});

const REFERENTIAL_ELEMENT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{11,12}$/i;

const mapDbReferential = (selectedReferential: IDbReferential) => ({
    children: [],
    id: selectedReferential.id,
    key: selectedReferential.key,
    name: selectedReferential.nameLabels,
    parents: selectedReferential.parentClientReferentialIds
});

export const getReferentialsByUniqueKey = (uniqueKey: string) => action<Promise<{ name: { label: string, lcid: ELcid }[] }>>(async (dispatch: Dispatch, getState: () => IState, http: Http) => {
    if (!uniqueKey) {
        return { name: [] };
    }

    const selectedReferential: IDbReferential = await getStoreDataByKey(Stores.ALLREFERENTIALSIDS, uniqueKey);
    if (selectedReferential) {
        return mapDbReferential(selectedReferential);
    }

    // `/referentials/elements/{id}` only accepts an element Id, not a unique key such as Platform.Currency.EUR.
    if (!REFERENTIAL_ELEMENT_ID.test(uniqueKey)) {
        return { name: [] };
    }

    return http.profilesApiGet(`/referentials/elements/${uniqueKey}`);
});

export const getReferentialIdentity = (type: EProfileDataType | EReferentials, context: EContextList = EContextList.Data) => action<Promise<IDBReferencialIdentity>>(async (dispatch: Dispatch, getState: () => IState, http: Http) => {
    const selectedReferential: IDbReferential = await getStoreDataByKey(Stores.ALLREFERENTIALS, context);
    if (selectedReferential) {
        return selectedReferential?.[type];
    } else {
        return dispatch<any>(getReferentialsIdentities('')).then(res => res.filter(elem => elem?.context === context && elem?.referentialType === type)[0]);
    }

});

export const setProgressData = (progressData: number) => ({
    type: SET_PROGRESS_DATA,
    progressData
});
